/**
 * Artist Browser Component
 *
 * Displays public domain drawings organized by artist.
 * Optimized for circular displays (Loop) and small devices.
 */

import { ALL_MASTER_DRAWINGS, type DrawingSource } from '../data/drawing-compendium';

export interface ArtistBrowserCallbacks {
  onSelectDrawing: (drawing: DrawingSource) => void;
  onClose: () => void;
  onToggleFavorite?: (drawing: DrawingSource) => void;
  isFavorite?: (drawing: DrawingSource) => boolean;
}

interface LocalDrawing {
  artist: string;
  title: string;
  path: string;
}

/**
 * Scan local drawings folder for downloaded files
 */
async function getLocalDrawings(): Promise<Map<string, LocalDrawing[]>> {
  const artistMap = new Map<string, LocalDrawing[]>();

  // Build from the compendium data - check which files exist locally
  for (const collection of ALL_MASTER_DRAWINGS) {
    if (collection.works.length === 0) continue;

    const artistSlug = collection.artist
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const drawings: LocalDrawing[] = [];

    for (const work of collection.works.slice(0, 10)) { // Limit to downloaded amount
      // Always use .jpg - the download script normalizes all extensions to .jpg
      const filename = work.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50) + '.jpg';

      // Use relative path for Android WebView/HTTP server compatibility
      const path = `./drawings/${artistSlug}/${filename}`;

      drawings.push({
        artist: collection.artist,
        title: work.title,
        path
      });
    }

    if (drawings.length > 0) {
      artistMap.set(collection.artist, drawings);
    }
  }

  return artistMap;
}

/**
 * Create the artist browser UI
 */
export function createArtistBrowser(
  container: HTMLElement,
  callbacks: ArtistBrowserCallbacks
): { destroy: () => void; refresh: () => void } {

  let selectedArtist: string | null = null;
  let artistDrawings: Map<string, LocalDrawing[]> = new Map();

  // Create browser structure with touch-friendly scrolling (no overlapping nav buttons)
  container.innerHTML = `
    <div class="artist-browser">
      <div class="artist-chips-container">
        <div class="artist-chips"></div>
      </div>
      <div class="scroll-hint-horizontal">← swipe →</div>
      <div class="artist-drawings-wrapper">
        <div class="artist-drawings-grid"></div>
      </div>
      <div class="scroll-hint-vertical">↑ swipe ↓</div>
      <div class="artist-empty">
        <p>Select an artist above</p>
      </div>
    </div>
  `;

  const chipsWrapper = container.querySelector('.artist-chips-container') as HTMLElement;
  const chipsContainer = container.querySelector('.artist-chips') as HTMLElement;
  const drawingsWrapper = container.querySelector('.artist-drawings-wrapper') as HTMLElement;
  const drawingsGrid = container.querySelector('.artist-drawings-grid') as HTMLElement;
  const emptyState = container.querySelector('.artist-empty') as HTMLElement;
  const hintHorizontal = container.querySelector('.scroll-hint-horizontal') as HTMLElement;
  const hintVertical = container.querySelector('.scroll-hint-vertical') as HTMLElement;

  /**
   * Render artist chips
   */
  function renderArtistChips() {
    chipsContainer.innerHTML = '';

    // Add "All" option first
    const allChip = document.createElement('button');
    allChip.className = `artist-chip ${selectedArtist === 'All' ? 'active' : ''}`;
    allChip.textContent = 'All';
    allChip.title = 'All Artists';
    allChip.addEventListener('click', () => {
      selectedArtist = 'All';
      renderArtistChips();
      renderDrawings();
    });
    chipsContainer.appendChild(allChip);

    const artists = Array.from(artistDrawings.keys());

    for (const artist of artists) {
      const chip = document.createElement('button');
      chip.className = `artist-chip ${selectedArtist === artist ? 'active' : ''}`;
      chip.textContent = getShortName(artist);
      chip.title = artist;

      chip.addEventListener('click', () => {
        selectedArtist = artist;
        renderArtistChips();
        renderDrawings();
      });

      chipsContainer.appendChild(chip);
    }
  }

  /**
   * Get shortened display name for artist
   */
  function getShortName(fullName: string): string {
    // Map long names to shorter versions for chips
    const shortNames: Record<string, string> = {
      'Leonardo da Vinci': 'da Vinci',
      'Albrecht Dürer': 'Dürer',
      'Peter Paul Rubens': 'Rubens',
      'Jean-Auguste-Dominique Ingres': 'Ingres',
      'Henri de Toulouse-Lautrec': 'Lautrec',
      'Käthe Kollwitz': 'Kollwitz',
      'Honoré Daumier': 'Daumier',
      'Edgar Degas': 'Degas',
      'William Blake': 'Blake',
      'Gustav Klimt': 'Klimt',
      'Egon Schiele': 'Schiele',
    };
    return shortNames[fullName] || fullName.split(' ').pop() || fullName;
  }

  /**
   * Render drawings grid for selected artist
   */
  function renderDrawings() {
    if (!selectedArtist) {
      drawingsWrapper.style.display = 'none';
      emptyState.style.display = 'flex';
      hintVertical.style.display = 'none';
      return;
    }

    // Get drawings - either from specific artist or all artists
    let drawings: LocalDrawing[];
    if (selectedArtist === 'All') {
      drawings = Array.from(artistDrawings.values()).flat();
    } else {
      drawings = artistDrawings.get(selectedArtist) || [];
    }

    if (drawings.length === 0) {
      drawingsWrapper.style.display = 'none';
      emptyState.innerHTML = '<p>No drawings downloaded yet.<br>Run the download script.</p>';
      emptyState.style.display = 'flex';
      hintVertical.style.display = 'none';
      return;
    }

    emptyState.style.display = 'none';
    drawingsWrapper.style.display = 'flex';
    drawingsGrid.style.display = 'grid';
    drawingsGrid.innerHTML = '';

    for (const drawing of drawings) {
      const item = document.createElement('div');
      item.className = 'artist-drawing-item';
      item.title = drawing.title;

      const img = document.createElement('img');
      img.src = drawing.path;
      img.alt = drawing.title;
      img.loading = 'lazy';

      // Handle load errors gracefully
      img.onerror = () => {
        item.style.display = 'none';
      };

      item.appendChild(img);

      // Add artist name label
      const label = document.createElement('div');
      label.className = 'artist-drawing-label';
      label.textContent = getShortName(drawing.artist);
      item.appendChild(label);

      // Add favorite heart button
      const source: DrawingSource = {
        title: drawing.title,
        artist: drawing.artist,
        url: drawing.path,
        thumbUrl: drawing.path,
        width: 0,
        height: 0
      };

      const heartBtn = document.createElement('button');
      heartBtn.className = 'artist-drawing-heart';
      const isFav = callbacks.isFavorite?.(source) ?? false;
      heartBtn.innerHTML = isFav ? '♥' : '♡';
      heartBtn.classList.toggle('favorited', isFav);
      heartBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        callbacks.onToggleFavorite?.(source);
        // Toggle visual state immediately
        const nowFav = callbacks.isFavorite?.(source) ?? false;
        heartBtn.innerHTML = nowFav ? '♥' : '♡';
        heartBtn.classList.toggle('favorited', nowFav);
      });
      item.appendChild(heartBtn);

      item.addEventListener('click', () => {
        callbacks.onSelectDrawing(source);
      });

      drawingsGrid.appendChild(item);
    }

    // Update scroll hints visibility after rendering
    requestAnimationFrame(updateScrollHints);
  }

  /**
   * Update scroll hints visibility based on content overflow
   */
  function updateScrollHints() {
    // Show horizontal hint if chips overflow
    const chipsOverflow = chipsContainer.scrollWidth > chipsWrapper.clientWidth;
    hintHorizontal.style.display = chipsOverflow ? 'block' : 'none';

    // Update fade indicators
    const chipsScrollLeft = chipsContainer.scrollLeft;
    const chipsScrollMax = chipsContainer.scrollWidth - chipsContainer.clientWidth;
    chipsWrapper.classList.toggle('scroll-left', chipsScrollLeft > 5);
    chipsWrapper.classList.toggle('scroll-right', chipsScrollLeft < chipsScrollMax - 5);

    // Show vertical hint if drawings overflow (check wrapper, not grid)
    const wrapperOverflow = drawingsWrapper.scrollHeight > drawingsWrapper.clientHeight;
    hintVertical.style.display = wrapperOverflow ? 'block' : 'none';

    // Update fade indicators
    const wrapperScrollTop = drawingsWrapper.scrollTop;
    const wrapperScrollMax = drawingsWrapper.scrollHeight - drawingsWrapper.clientHeight;
    drawingsWrapper.classList.toggle('scroll-top', wrapperScrollTop > 5);
    drawingsWrapper.classList.toggle('scroll-bottom', wrapperScrollTop < wrapperScrollMax - 5);
  }

  /**
   * Set up scroll event listeners for touch scrolling
   */
  function setupScrollListeners() {
    // Update hints on scroll
    chipsContainer.addEventListener('scroll', updateScrollHints);
    drawingsWrapper.addEventListener('scroll', updateScrollHints);

    // Initial update
    requestAnimationFrame(updateScrollHints);
  }

  /**
   * Initialize the browser
   */
  async function init() {
    artistDrawings = await getLocalDrawings();

    // Default to "All" view
    selectedArtist = 'All';

    renderArtistChips();
    renderDrawings();
    setupScrollListeners();
  }

  init();

  return {
    destroy: () => {
      container.innerHTML = '';
    },
    refresh: () => {
      init();
    }
  };
}
